import React, { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { FaEye, FaInstagram, FaPen, FaPhoneAlt, FaWhatsapp } from 'react-icons/fa';
import api, { addDays, formatDateTime, formatPhone, isoDate } from '../services/api';
import ChatModal from '../components/ChatModal';
import RecordingModal from '../components/RecordingModal';
import {
  AppointmentStatus,
  ChipRow,
  DateRange,
  INSTAGRAM_COLOR,
  InstagramButton,
  InterestTag,
  InterventionBadge,
  LeadFlag,
  NO_SHOW_STAGE,
  STAGES,
  Loader,
  Modal,
  PageHead,
  Pagination,
  SearchBox,
  SourceBadge,
  Spinner,
  ToggleChip,
  UnassignedPill,
  VoiceButton,
  WhatsAppButton,
  contactLabel,
  exportCSV,
  instagramHandle,
  inDateRange,
  usePagination,
} from '../components/ui';

const VIA_CHIPS = [
  { key: '', label: 'All' },
  { key: 'whatsapp', label: 'WhatsApp' },
  { key: 'instagram', label: 'Instagram' },
  { key: 'voice', label: 'Voice Call' },
  { key: 'walk_in', label: 'Walk-in / desk' },
];
const VIA_LABEL = { whatsapp: 'WhatsApp', instagram: 'Instagram', voice: 'Voice call', walk_in: 'Walk-in / desk' };
const DESK_COLOR = '#b01e55';
// Leads also show No-show: a lead whose every appointment was missed did not really convert.
const LEAD_STAGES = [...STAGES, NO_SHOW_STAGE];
const STATUS_CHIPS = [{ key: '', label: 'All statuses' }, ...LEAD_STAGES.map((s) => ({ key: s.key, label: s.label }))];
const CSV_COLUMNS = [
  { label: 'Name', key: 'name' },
  { label: 'Phone', key: 'phone' },
  { label: 'Instagram', value: (r) => instagramHandle(r.instagram_username) },
  { label: 'Status', value: (r) => (LEAD_STAGES.find((s) => s.key === r.stage) || STAGES[1]).label },
  { label: 'Services', value: (r) => (r.services || []).join(', ') },
  { label: 'Source', value: (r) => VIA_LABEL[r.via] || 'WhatsApp' },
  { label: 'Last activity', key: 'last_at' },
  { label: 'Remarks', key: 'note' },
];

function MiniStat({ label, value, color, active, onClick, icon }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        background: 'rgba(255, 255, 255, 0.24)',
        border: `1px solid ${active ? color : 'rgba(255, 255, 255, 0.9)'}`,
        boxShadow: active ? `0 0 0 1px ${color}` : 'none',
        borderRadius: 10,
        padding: '12px 16px',
        flex: '1 1 110px',
        minWidth: 100,
        textAlign: 'left',
        cursor: 'pointer',
      }}
    >
      <div style={{ fontSize: 22, fontWeight: 700, color, lineHeight: 1.1 }}>{value}</div>
      <div className="nye-row" style={{ gap: 5, fontSize: 11, color: 'var(--nye-soft)', marginTop: 3, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
        {icon}
        {label}
      </div>
    </button>
  );
}

function MiniBar({ label, value, max, color, active, onClick }) {
  const pct = max > 0 ? Math.max(2, Math.round((value / max) * 100)) : 0;
  return (
    <button
      type="button"
      onClick={onClick}
      style={{ width: '100%', background: 'transparent', border: `1px solid ${active ? color : 'transparent'}`, borderRadius: 8, padding: 6, marginBottom: 6, cursor: 'pointer', textAlign: 'left' }}
    >
      <div className="nye-row nye-row--between" style={{ marginBottom: 3, fontSize: 12 }}>
        <span>{label}</span>
        <span className="nye-strong">{value}</span>
      </div>
      <div style={{ background: 'var(--nye-border)', borderRadius: 4, height: 7, overflow: 'hidden' }}>
        <div style={{ width: `${pct}%`, height: '100%', background: color, borderRadius: 4, transition: 'width .4s' }} />
      </div>
    </button>
  );
}

function Analytics({ leads, filters, apply }) {
  const total = leads.length;
  const whatsapp = leads.filter((l) => l.via === 'whatsapp').length;
  const voice = leads.filter((l) => l.via === 'voice').length;
  const insta = leads.filter((l) => l.via === 'instagram').length;
  const walkIn = leads.filter((l) => l.via === 'walk_in').length;
  const channelMax = Math.max(whatsapp, insta, voice, walkIn);
  const byStage = LEAD_STAGES.map((s) => ({ ...s, value: leads.filter((l) => l.stage === s.key).length }));
  const stageMax = Math.max(1, ...byStage.map((s) => s.value));
  const human = leads.filter((l) => l.handoff_open).length;
  const today = isoDate();
  const days = Array.from({ length: 28 }, (_, i) => addDays(today, i - 27));
  const byDay = days.map((d) => ({ date: d, count: leads.filter((l) => String(l.created_at).slice(0, 10) === d).length }));
  const maxDay = Math.max(1, ...byDay.map((d) => d.count));

  return (
    <div style={{ marginBottom: 16 }}>
      <div className="nye-row nye-row--wrap" style={{ gap: 8, marginBottom: 12 }}>
        <MiniStat label="Total leads" value={total} color="var(--nye-purple)" active={!filters.via && !filters.status} onClick={() => apply({ via: '', status: '' })} />
        <MiniStat label="WhatsApp" value={whatsapp} color="#1fa855" icon={<FaWhatsapp size={12} />} active={filters.via === 'whatsapp'} onClick={() => apply({ via: 'whatsapp' })} />
        <MiniStat label="Instagram" value={insta} color={INSTAGRAM_COLOR} icon={<FaInstagram size={12} />} active={filters.via === 'instagram'} onClick={() => apply({ via: 'instagram' })} />
        <MiniStat label="Voice" value={voice} color="#258a85" icon={<FaPhoneAlt size={10} />} active={filters.via === 'voice'} onClick={() => apply({ via: 'voice' })} />
        <MiniStat label="Walk-in" value={walkIn} color={DESK_COLOR} active={filters.via === 'walk_in'} onClick={() => apply({ via: 'walk_in' })} />
        {byStage.map((s) => (
          <MiniStat key={s.key} label={s.label} value={s.value} color={s.color} active={filters.status === s.key} onClick={() => apply({ status: s.key })} />
        ))}
        <MiniStat label="Need a person" value={human} color="var(--nye-danger)" active={filters.human} onClick={() => apply({ human: !filters.human })} />
      </div>
      <div className="nye-g2 nye-g2--auto" style={{ gap: 10, gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))' }}>
        <div className="nye-card" style={{ padding: 14, boxShadow: 'none' }}>
          <div className="nye-section-label">By status</div>
          {byStage.map((s) => (
            <MiniBar key={s.key} label={s.label} value={s.value} max={stageMax} color={s.color} active={filters.status === s.key} onClick={() => apply({ status: s.key })} />
          ))}
        </div>
        <div className="nye-card" style={{ padding: 14, boxShadow: 'none' }}>
          <div className="nye-section-label">By channel</div>
          <MiniBar label="WhatsApp" value={whatsapp} max={channelMax} color="#25D366" active={filters.via === 'whatsapp'} onClick={() => apply({ via: 'whatsapp' })} />
          <MiniBar label="Instagram" value={insta} max={channelMax} color={INSTAGRAM_COLOR} active={filters.via === 'instagram'} onClick={() => apply({ via: 'instagram' })} />
          <MiniBar label="Voice call" value={voice} max={channelMax} color="#258a85" active={filters.via === 'voice'} onClick={() => apply({ via: 'voice' })} />
          <MiniBar label="Walk-in / desk" value={walkIn} max={channelMax} color={DESK_COLOR} active={filters.via === 'walk_in'} onClick={() => apply({ via: 'walk_in' })} />
        </div>
        <div className="nye-card" style={{ padding: 14, boxShadow: 'none' }}>
          <div className="nye-section-label">Daily trend · last 28 days</div>
          <div style={{ display: 'flex', gap: 2, alignItems: 'flex-end', height: 56 }}>
            {byDay.map((d) => (
              <div
                key={d.date}
                title={`${d.date}: ${d.count}`}
                style={{ flex: 1, minWidth: 3, height: `${Math.max(3, Math.round((d.count / maxDay) * 52))}px`, background: 'var(--nye-purple-brand)', borderRadius: '2px 2px 0 0', opacity: d.count ? 0.85 : 0.2 }}
              />
            ))}
          </div>
          <div className="nye-row nye-row--between" style={{ fontSize: 10, color: 'var(--nye-soft)', marginTop: 4 }}>
            <span>{days[0].slice(5)}</span>
            <span>{days[days.length - 1].slice(5)}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

function RemarkModal({ lead, onSaved, onClose }) {
  const [text, setText] = useState(lead.note || '');
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try {
      await api.updateLead(lead.id, { note: text });
      toast.success(lead.note ? 'Remark updated' : 'Remark added');
      onSaved();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  };
  return (
    <Modal title="Lead remark" onClose={onClose}>
      <div className="nye-small nye-muted" style={{ marginBottom: 10 }}>
        {lead.name || 'No name yet'} · {contactLabel(lead.phone, lead.instagram_username)}
      </div>
      <textarea className="nye-textarea" value={text} onChange={(e) => setText(e.target.value)} placeholder="Add a note for the team…" autoFocus />
      <div className="nye-form-actions" style={{ marginTop: 18 }}>
        <button type="button" className="nye-btn nye-btn--outline" onClick={onClose}>
          Cancel
        </button>
        <button type="button" className="nye-btn nye-btn--primary" onClick={save} disabled={saving}>
          {saving ? <Spinner light /> : null}
          Save remark
        </button>
      </div>
    </Modal>
  );
}

export default function Leads() {
  const [leads, setLeads] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [calls, setCalls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAnalytics, setShowAnalytics] = useState(false);
  const [via, setVia] = useState('');
  const [status, setStatus] = useState('');
  const [human, setHuman] = useState(false);
  const [search, setSearch] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [chatFor, setChatFor] = useState(null);
  const [recording, setRecording] = useState(null);
  const [remarkFor, setRemarkFor] = useState(null);
  const [details, setDetails] = useState(null);

  const load = (silent) => {
    if (!silent) setLoading(true);
    return Promise.all([api.listLeads(), api.listAppointments(), api.listCalls()])
      .then(([l, a, c]) => {
        setLeads(l);
        setAppointments(a);
        setCalls(c);
      })
      .catch((e) => toast.error(e.message || 'Failed to load leads'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load(false);
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return leads.filter((l) => {
      if (via && l.via !== via) return false;
      if (status && l.stage !== status) return false;
      if (human && !l.handoff_open) return false;
      if (!inDateRange(l.last_at, dateFrom, dateTo)) return false;
      if (!q) return true;
      return [l.name, l.phone, l.note, ...(l.services || [])].some((v) => String(v || '').toLowerCase().includes(q));
    });
  }, [leads, via, status, human, search, dateFrom, dateTo]);

  const { page, setPage, pageSize, setPageSize, paginated, totalPages, offset } = usePagination(filtered, 25);

  const apply = (next) => {
    if ('via' in next) setVia(next.via);
    if ('status' in next) setStatus(next.status);
    if ('human' in next) setHuman(next.human);
  };

  const playCall = (lead) => {
    const call = calls.find((c) => c.customer_phone === lead.phone);
    if (!call) {
      toast.error('No call record found for this lead');
      return;
    }
    setRecording(call);
  };

  const booked = leads.filter((l) => l.status === 'booked').length;
  const customerAppointments = details ? appointments.filter((a) => a.customer_id === details.customer_id) : [];

  return (
    <div className="nye-fade">
      <PageHead
        title="Leads"
        sub={`${leads.length} lead${leads.length !== 1 ? 's' : ''} · ${booked} booked · from WhatsApp, Instagram, voice calls and walk-ins`}
        action={
          <div className="nye-row nye-row--wrap">
            <button type="button" className="nye-btn nye-btn--outline" onClick={() => load(false)}>
              ↻ Refresh
            </button>
            <button type="button" className={`nye-btn ${showAnalytics ? 'nye-btn--gold' : 'nye-btn--outline'}`} onClick={() => setShowAnalytics((v) => !v)}>
              📊 {showAnalytics ? 'Hide Report' : 'Analytics'}
            </button>
            <button
              type="button"
              className="nye-btn nye-btn--primary"
              onClick={() => {
                if (!filtered.length) return toast.error('No leads to export');
                exportCSV(filtered, CSV_COLUMNS, 'leads');
                return toast.success(`Exported ${filtered.length} leads`);
              }}
            >
              ↓ Export CSV
            </button>
          </div>
        }
      />

      {showAnalytics && !loading ? <Analytics leads={leads} filters={{ via, status, human }} apply={apply} /> : null}

      <ChipRow label="Via" chips={VIA_CHIPS} value={via} onChange={setVia} />
      <div className="nye-row nye-row--wrap" style={{ gap: 10, marginBottom: 10 }}>
        <SearchBox value={search} onChange={setSearch} placeholder="Search by name, phone, service or remark…" style={{ maxWidth: 'none', flex: '1 1 300px' }} />
        <DateRange
          from={dateFrom}
          to={dateTo}
          onFrom={setDateFrom}
          onTo={setDateTo}
          onClear={() => {
            setDateFrom('');
            setDateTo('');
          }}
        />
      </div>
      <div className="nye-row" style={{ marginBottom: 12 }}>
        <ToggleChip active={human} onClick={() => setHuman((v) => !v)}>
          🆘 Human intervention needed
        </ToggleChip>
      </div>
      <ChipRow chips={STATUS_CHIPS} value={status} onChange={setStatus} style={{ marginBottom: 16 }} />

      {loading ? (
        <Loader />
      ) : (
        <div className="nye-table-shell">
          <div className="nye-table-wrap">
            <table className="nye-table">
              <thead>
                <tr>
                  <th style={{ width: 56, textAlign: 'center' }}>S.No</th>
                  <th>Name</th>
                  <th>Phone / ID</th>
                  <th>Status</th>
                  <th>Services</th>
                  <th>Source</th>
                  <th>Last activity</th>
                  <th style={{ textAlign: 'right' }}>Conversation</th>
                  <th style={{ textAlign: 'center' }}>Remarks</th>
                </tr>
              </thead>
              <tbody>
                {paginated.length === 0 ? (
                  <tr>
                    <td colSpan={9} className="nye-table-empty">
                      No leads found{search || via || status || human ? ' — try adjusting your filters' : ''}.
                    </td>
                  </tr>
                ) : (
                  paginated.map((l, i) => (
                    <tr key={l.id} className="nye-row-click" onClick={() => setDetails(l)}>
                      <td className="nye-td-center nye-td-muted">{offset + i + 1}</td>
                      <td className="nye-td-strong">{l.name || <span className="nye-muted" style={{ fontStyle: 'italic', fontWeight: 400 }}>No name yet</span>}</td>
                      <td className="nye-td-mono">{contactLabel(l.phone, l.instagram_username)}</td>
                      <td>
                        <div className="nye-row nye-row--wrap" style={{ gap: 6 }}>
                          <LeadFlag status={l.stage} reason={l.stage_reason} />
                          {l.stage === 'booked' ? <InterestTag service={l.interest_service} /> : null}
                          {l.handoff_open ? <InterventionBadge compact /> : null}
                        </div>
                      </td>
                      <td style={{ maxWidth: 220 }} className="nye-muted">
                        {(l.services || []).length ? (
                          <span title={l.services.join(', ')}>
                            {l.services.slice(0, 2).join(', ')}
                            {l.services.length > 2 ? <span className="nye-muted"> +{l.services.length - 2} more</span> : null}
                          </span>
                        ) : (
                          '—'
                        )}
                      </td>
                      <td>
                        <SourceBadge source={l.via} showLabel />
                      </td>
                      <td className="nye-td-muted" style={{ whiteSpace: 'nowrap' }}>
                        {formatDateTime(l.last_at)}
                      </td>
                      <td className="nye-td-right" onClick={(e) => e.stopPropagation()}>
                        {l.has_chat && l.via === 'instagram' ? <InstagramButton onClick={() => setChatFor(l)} /> : null}
                        {l.has_chat && l.via !== 'instagram' ? <WhatsAppButton onClick={() => setChatFor(l)} /> : null}
                        {l.has_call ? <VoiceButton onClick={() => playCall(l)} /> : null}
                        {!l.has_chat && !l.has_call ? <span className="nye-muted">—</span> : null}
                      </td>
                      <td className="nye-td-center" onClick={(e) => e.stopPropagation()}>
                        <button
                          type="button"
                          className="nye-icon-action nye-icon-action--plain"
                          title={l.note ? `Remark: ${l.note}` : 'Add remark'}
                          onClick={() => setRemarkFor(l)}
                          style={{ color: l.note ? 'var(--nye-gold-deep)' : 'var(--nye-soft)' }}
                        >
                          {l.note ? <FaEye size={14} /> : <FaPen size={12} />}
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          <Pagination attached page={page} setPage={setPage} pageSize={pageSize} setPageSize={setPageSize} totalPages={totalPages} />
        </div>
      )}

      {details ? (
        <Modal title={details.name || contactLabel(details.phone, details.instagram_username)} onClose={() => setDetails(null)} size="xwide">
          <div className="nye-row nye-row--wrap" style={{ gap: 10, marginBottom: 16, paddingBottom: 14, borderBottom: '1px solid var(--nye-border)' }}>
            <SourceBadge source={details.via} showLabel />
            <LeadFlag status={details.stage || details.status} reason={details.stage_reason} />
            <span className="nye-mono nye-muted">{contactLabel(details.phone, details.instagram_username)}</span>
            {details.note ? <span className="nye-small nye-gold-text">Remark: {details.note}</span> : null}
          </div>
          <div className="nye-section-label">Appointment details</div>
          {customerAppointments.length === 0 ? (
            <div className="nye-card" style={{ textAlign: 'center', color: 'var(--nye-soft)', boxShadow: 'none' }}>
              No appointment history found for this contact.
            </div>
          ) : (
            <div className="nye-table-shell">
              <div className="nye-table-wrap" style={{ maxHeight: '52vh', overflowY: 'auto' }}>
                <table className="nye-table">
                  <thead>
                    <tr>
                      <th>Appointment ID</th>
                      <th>Date</th>
                      <th>Status</th>
                      <th>Staff</th>
                      <th>Service</th>
                      <th>Source</th>
                    </tr>
                  </thead>
                  <tbody>
                    {customerAppointments.map((a) => (
                      <tr key={a.id}>
                        <td className="nye-td-mono">#{a.id}</td>
                        <td style={{ whiteSpace: 'nowrap' }}>{formatDateTime(a.starts_at)}</td>
                        <td>
                          <AppointmentStatus status={a.status} />
                        </td>
                        <td>{a.staff_name || <UnassignedPill />}</td>
                        <td>{a.service_name}</td>
                        <td>
                          <SourceBadge source={a.source} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="nye-small nye-muted" style={{ padding: '10px 16px', borderTop: '1px solid var(--nye-border)' }}>
                {customerAppointments.length} appointment{customerAppointments.length !== 1 ? 's' : ''}
              </div>
            </div>
          )}
          <div className="nye-row nye-row--wrap nye-mt-16" style={{ gap: 10 }}>
            {details.has_chat ? (
              <button type="button" className="nye-btn nye-btn--outline nye-btn--sm" onClick={() => setChatFor(details)}>
                <FaWhatsapp color="#25D366" /> Open conversation
              </button>
            ) : null}
            {details.has_call ? (
              <button type="button" className="nye-btn nye-btn--outline nye-btn--sm" onClick={() => playCall(details)}>
                <FaPhoneAlt color="#258a85" /> Call record
              </button>
            ) : null}
            <button type="button" className="nye-btn nye-btn--outline nye-btn--sm" onClick={() => setRemarkFor(details)}>
              <FaPen /> {details.note ? 'Edit remark' : 'Add remark'}
            </button>
          </div>
        </Modal>
      ) : null}

      {chatFor ? (
        <ChatModal customerId={chatFor.customer_id} channel={chatFor.via === 'instagram' ? 'instagram' : 'whatsapp'} flag={chatFor.stage} onClose={() => setChatFor(null)} onChanged={() => load(true)} />
      ) : null}
      {recording ? <RecordingModal call={recording} onClose={() => setRecording(null)} /> : null}
      {remarkFor ? (
        <RemarkModal
          lead={remarkFor}
          onClose={() => setRemarkFor(null)}
          onSaved={() => {
            setRemarkFor(null);
            setDetails(null);
            load(true);
          }}
        />
      ) : null}
    </div>
  );
}
